import { useAuthorization, ROLES } from 'lib/authorization';
import { HomeIcon } from '@heroicons/react/outline';
import clsx from 'clsx';
import * as React from 'react';
import {
  FileText,
  Users,
  Globe,
  Monitor,
  Grid,
  Package,
  Triangle,
  Phone,
  TrendingUp,
  ShoppingBag,
  Shield,
  Box,
  ThumbsUp,
} from 'react-feather';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';

export const SideNavigation = ({ showLabels = true, onLinkClick }) => {
  const { checkAccess } = useAuthorization();

  // Navigation list (logic unchanged)
  const navigation = [
    { name: 'Overview', to: '.', icon: HomeIcon },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Products',
      to: './products',
      icon: Grid,
      heading: 'Shop',
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Orders',
      to: './orders',
      icon: ShoppingBag,
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Sales',
      to: './sales',
      icon: TrendingUp,
    },
    checkAccess({ allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN] }) && {
      name: 'Customers',
      to: './users',
      icon: Users,
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Moderators',
      to: './moderators',
      icon: Shield,
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Blog',
      to: './blog',
      icon: FileText,
      heading: 'Publications',
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Partners',
      to: './partners',
      icon: Users,
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Reviews',
      to: './reviews',
      icon: ThumbsUp,
    },

    // Pages section
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Home',
      to: './home-page',
      icon: Monitor,
      heading: 'Pages',
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'About',
      to: './about-page',
      icon: Globe,
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Services',
      to: './services-page',
      icon: Package,
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Impact',
      to: './impact-page',
      icon: Triangle,
    },

    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Contact Us',
      to: './contact-page',
      icon: Phone,
    },
    checkAccess({ allowedRoles: [ROLES.SUPER_ADMIN] }) && {
      name: 'Integrations',
      to: './integrations',
      icon: Box,
      heading: 'System',
    },
  ].filter(Boolean);

  return (
    <nav className="pt-6 pb-10 text-gray-300">
      {navigation.map((item, index) => (
        <div key={index} className="w-full">
          {/* Section heading */}
          {item.heading && (
            <div className="px-4 mb-2 mt-6">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                {showLabels && item.heading}
              </p>
              <div className="h-px bg-gray-800 mt-2" />
            </div>
          )}

          {/* Nav item */}
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
          >
            <NavLink
              end={index === 0}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center gap-3 px-4 py-2 mb-1 rounded-lg transition-all duration-200',
                  isActive
                    ? 'bg-[#AE3F1E]/15 text-[#AE3F1E] font-semibold'
                    : 'hover:bg-gray-800/50 hover:text-white text-gray-300'
                )
              }
              onClick={() => onLinkClick && onLinkClick()}
            >
              {/* Icon */}
              <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gray-800 group-hover:bg-gray-700 transition">
                <item.icon
                  className={clsx(
                    'h-5 w-5 transition-colors',
                    'text-gray-400 group-hover:text-[#AE3F1E]'
                  )}
                />
              </div>

              {/* Label */}
              {showLabels && <span className="text-sm">{item.name}</span>}
            </NavLink>
          </motion.div>
        </div>
      ))}
    </nav>
  );
};
